import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileSignature, Loader2, Paperclip, Send, Undo2, X, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import AttachmentView from '@/components/chat/AttachmentView';
import ChatAvatar from '@/components/chat/ChatAvatar';
import { fetchMessages, markRead, sendMessage } from '@/data/advisor-chat';
import type { ChatConversation, ChatEventKind, ChatMessage } from '@/types/chat';

export const MESSAGE_POLL_MS = 4000;
const MAX_FILES = 5;
const MAX_BYTES = 25 * 1024 * 1024;


const EVENT_STYLE: Record<Exclude<ChatEventKind, 'text'>, { icon: typeof FileSignature; tone: string; title: string }> = {
  offer_sent: { icon: FileSignature, tone: 'border-amber-300 bg-amber-50 text-amber-950', title: 'New offer' },
  offer_requested: { icon: FileSignature, tone: 'border-sky-300 bg-sky-50 text-sky-950', title: 'Offer requested' },
  offer_confirmed: { icon: CheckCircle2, tone: 'border-green-300 bg-green-50 text-green-950', title: 'Contract confirmed' },
  offer_rejected: { icon: XCircle, tone: 'border-red-200 bg-red-50 text-red-950', title: 'Advisor could not take it on' },
  offer_accepted: { icon: CheckCircle2, tone: 'border-green-300 bg-green-50 text-green-950', title: 'Contract agreed' },
  offer_declined: { icon: XCircle, tone: 'border-slate-300 bg-slate-50 text-slate-900', title: 'Offer declined' },
  offer_withdrawn: { icon: Undo2, tone: 'border-slate-300 bg-slate-50 text-slate-900', title: 'Offer withdrawn' },
  contract_ended: { icon: XCircle, tone: 'border-red-200 bg-red-50 text-red-950', title: 'Contract ended' },
};

function contractsPathFor(conversation: ChatConversation | undefined) {
  if (!conversation) return '/contracts';
  if (conversation.your_side === 'advisor' && conversation.advisor_client_id) {
    return `/advisor/clients/${conversation.advisor_client_id}/offers`;
  }
  return '/contracts';
}

function EventBubble({ message, conversation }: { message: ChatMessage; conversation?: ChatConversation }) {
  const style = EVENT_STYLE[message.kind as Exclude<ChatEventKind, 'text'>];
  const Icon = style.icon;
  const readAs = conversation?.your_side === 'advisor' ? 'Open offers & contracts' : 'Open contracts';
  return (
    <div className="flex justify-center" data-testid="chat-event" data-kind={message.kind}>
      <div className={`w-full max-w-sm rounded-xl border px-4 py-3 text-sm ${style.tone}`}>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80">
          <Icon className="h-4 w-4" />
          {style.title}
        </div>
        <p className="mt-1 leading-snug">{message.body}</p>
        <Link
          to={contractsPathFor(conversation)}
          className="mt-2 inline-block text-xs font-medium underline underline-offset-2"
          data-testid="chat-event-link"
        >
          {readAs}
        </Link>
      </div>
    </div>
  );
}

function Bubble({
  message,
  mine,
  counterparty,
  counterpartyPhotoUrl,
  conversation,
}: {
  message: ChatMessage;
  mine: boolean;
  counterparty: string;
  counterpartyPhotoUrl: string;
  conversation?: ChatConversation;
}) {
  if (message.side === 'system' && message.kind && message.kind !== 'text') {
    return <EventBubble message={message} conversation={conversation} />;
  }
  if (message.side === 'system') {
    return (
      <div className="text-center" data-testid="chat-system-message">
        <span className="inline-block rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
          {message.body}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
      {!mine && (
        <ChatAvatar
          name={message.sender_name || counterparty}
          photoUrl={message.sender_avatar_url || counterpartyPhotoUrl}
          className="mb-4 h-7 w-7"
        />
      )}
      <div className="max-w-[85%] space-y-1">
        {!mine && message.sender_name && (
          <p className="px-1 text-[11px] text-muted-foreground">{message.sender_name}</p>
        )}
        <div
          className={`whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm ${
            mine ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}
          data-testid="chat-message"
        >
          {message.body}
          {message.attachments.length > 0 && (
            <div
              className={`mt-2 space-y-2 ${message.body ? 'border-t pt-2' : ''} ${
                mine ? 'border-primary-foreground/20' : 'border-border'
              }`}
            >
              {message.attachments.map((attachment) => (
                <AttachmentView key={attachment.id} attachment={attachment} />
              ))}
            </div>
          )}
        </div>
        <p className={`px-1 text-[10px] text-muted-foreground ${mine ? 'text-right' : ''}`}>
          {new Date(message.created_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

interface Props {
  conversationId: string;
  header?: React.ReactNode;
  onLoaded?: (conversation: import('@/types/chat').ChatConversation) => void;
}

export default function ChatThread({ conversationId, header, onLoaded }: Props) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState('');
  const [files, setFiles] = React.useState<File[]>([]);
  const fileInput = React.useRef<HTMLInputElement>(null);
  const bottom = React.useRef<HTMLDivElement>(null);

  const thread = useQuery({
    queryKey: ['chat-messages', conversationId],
    queryFn: () => fetchMessages(conversationId),
    refetchInterval: MESSAGE_POLL_MS,
    retry: false,
  });

  const messages = thread.data?.messages ?? [];
  const conversation = thread.data?.conversation;

  React.useEffect(() => {
    if (conversation) onLoaded?.(conversation);
  }, [conversation, onLoaded]);

  React.useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  React.useEffect(() => {
    if (!messages.length) return;
    markRead(conversationId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['chat-unread'] });
        queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      })
      .catch(() => undefined);
  }, [conversationId, messages.length, queryClient]);

  React.useEffect(() => {
    setDraft('');
    setFiles([]);
  }, [conversationId]);

  const send = useMutation({
    mutationFn: async () => {
      const body = draft.trim();
      const attachments = files;
      setDraft('');
      setFiles([]);
      try {
        await sendMessage(conversationId, body, attachments);
      } catch (error) {
        setDraft((current) => current || body);
        setFiles((current) => (current.length ? current : attachments));
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
    onError: (error) => toast.error(apiError(error, 'Could not send that.')),
  });

  const pickFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = '';
    const tooBig = picked.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      toast.error(`${tooBig.name} is larger than 25 MB.`);
      return;
    }
    if (files.length + picked.length > MAX_FILES) {
      toast.warning(`Up to ${MAX_FILES} files per message.`);
    }
    setFiles([...files, ...picked].slice(0, MAX_FILES));
  };

  const canSend =
    conversation?.is_open !== false &&
    (draft.trim().length > 0 || files.length > 0) &&
    !send.isPending;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}

      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
        {thread.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="chat-no-messages">
            No messages yet. Say hello.
          </p>
        ) : (
          messages.map((message) => (
            <Bubble
              key={message.id}
              message={message}
              mine={message.side === conversation?.your_side}
              counterparty={conversation?.counterparty ?? ''}
              counterpartyPhotoUrl={conversation?.counterparty_photo_url ?? ''}
            />
          ))
        )}
        <div ref={bottom} />
      </div>

      {conversation?.is_open === false ? (
        <p className="border-t px-4 py-3 text-center text-xs text-muted-foreground" data-testid="chat-closed-notice">
          This conversation is closed. You can read it, but not add to it.
        </p>
      ) : (
        <div className="space-y-2 border-t p-3">
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="chat-pending-attachments">
              {files.map((file, index) => (
                <Badge key={`${file.name}-${index}`} variant="outline" className="gap-1">
                  <Paperclip className="h-3 w-3" />
                  <span className="max-w-[140px] truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => setFiles(files.filter((_, i) => i !== index))}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && canSend) {
                e.preventDefault();
                send.mutate();
              }
            }}
            placeholder="Write a message…"
            rows={2}
            className="resize-none"
            data-testid="chat-input"
          />
          <div className="flex items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              multiple
              className="hidden"
              onChange={pickFiles}
              data-testid="chat-file-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInput.current?.click()}
              data-testid="chat-attach"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              onClick={() => send.mutate()}
              disabled={!canSend}
              data-testid="chat-send"
            >
              {send.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1" />
              )}
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
